import { useState } from 'react';

import type { Client, PlayerId } from '@suntex.fim/api-client';

import './VifRulesShell.scss';
import { VifEndScreen } from './components/VifEndScreen';
import { VifGridBoard } from './components/VifGridBoard';
import { VifPracticeMenu } from './components/VifPracticeMenu';
import { type VifPanelCopy, VifRulesPanel } from './components/VifRulesPanel';
import { VifScoreboard } from './components/VifScoreboard';
import type { RulesGame } from './hooks/useRulesBoardGame';
import type { VifRulesModule } from './hooks/useVifRulesModule';

/**
 * What every "3xN rules" family view, and the 4x4 block view, takes. Each
 * adds only its own `flag`, which is the sole thing that differs between them.
 */
export interface VifRulesFamilyViewProps {
  api: Client;
  playerId: PlayerId;
  /** Identifies the module whose progress is read and written. */
  moduleId: number;
  moduleVariant: number;
  /**
   * Preview/admin logins open on the practice menu and can change round
   * mid-module, matching `GameFrame`'s practice mode.
   */
  preview?: boolean;
  /** Host's sound directory, e.g. the CDN's `sounds/`. */
  soundDirectory: string;
  /** The player's VIF hub, for the More Games links. */
  hubUrl?: string;
  /**
   * Called once, when the end screen's Continue button is pressed on the last
   * game of the last round. Legacy `continueVIF`.
   */
  onComplete: () => void;
}

export interface VifRulesShellProps {
  rules: VifRulesModule;
  /**
   * The family's rules-panel copy for the variant in play. The 4x4 block
   * board passes only its operation - it has no rule text.
   */
  copy: VifPanelCopy;
  /** The player's VIF hub, for the More Games links. */
  hubUrl?: string;
  /**
   * Show the layer, not the game, in the scoreboard's game field. Legacy
   * add/sub `finalizeNextProblem` writes `intLayer` there on every board
   * (`games/VIFRulesAddSub.ts:595`); mul/div and the block board leave it at
   * `gameFrame.game`.
   */
  scoreboardShowsLayer?: boolean;
}

/**
 * The screen every "3xN rules" VIF family and the 4x4 block board render: the
 * board, the rules panel, the practice chooser and the end screen, driven by
 * `useVifRulesModule`.
 *
 * Add/sub and mul/div differ in their rules, pacing config and problem
 * selection - none of which is here. Held per family, these two copies of the
 * shell had to be edited in parallel for any lifecycle change, which is how
 * they came to drift.
 */
export function VifRulesShell({
  rules,
  copy,
  hubUrl,
  scoreboardShowsLayer = false,
}: VifRulesShellProps) {
  const { progress, game, board, playing } = rules;
  const scoreboardGame = useScoreboardGame(game, scoreboardShowsLayer);

  // The position decides which round's problems and clock apply, so there is
  // nothing meaningful to deal until it has been read.
  if (progress.loading) {
    return <div className="vif-rules-view" />;
  }

  return (
    <div className="vif-rules-view">
      {/* Legacy `GameFrame` hides the scoreboard's panel while the practice
          menu is up and shows it again from `startGame`. */}
      {game.phase === 'practice' ? null : (
        <div className="vif-rules-view__header">
          <VifScoreboard round={game.round} game={scoreboardGame} />
        </div>
      )}

      {/* The surface time on task is measured against: the board, the panel
          and the dialogues, and nothing else on the page. */}
      <div className="vif-rules-view__stage" ref={rules.surfaceRef}>
        <div className="vif-rules-view__board">
          <VifGridBoard
            state={board.state}
            onDrop={rules.onDrop}
            large={game.config.large}
            preString={game.config.preString}
            revealed={playing}
            onSpeak={rules.onSpeak}
          />
        </div>

        <VifRulesPanel
          copy={copy}
          round={game.round}
          roundsInModule={game.config.roundsInModule}
          layer={game.layer}
          layersInGame={game.config.layersInGame}
          secondsLeft={game.secondsLeft}
          canStart={game.phase === 'ready'}
          onStart={game.start}
          onChangeRound={progress.practice ? game.openPracticeMenu : undefined}
        />

        {game.phase === 'practice' ? (
          <VifPracticeMenu
            roundsInModule={game.config.roundsInModule}
            onChooseRound={game.chooseRound}
            hubUrl={hubUrl}
          />
        ) : null}

        {game.endScreen ? (
          <VifEndScreen
            copy={game.endScreen}
            finalGame={game.finalGame}
            preview={progress.practice}
            stickers={progress.stickers}
            saving={progress.saving}
            hubUrl={hubUrl}
            onNext={game.nextGame}
            onContinue={game.continueModule}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * What the scoreboard's game field shows, or `null` to hide it.
 *
 * Both rules families' `startGameBody` call `scoreboard.updateGame(null)` when
 * the round has one game, so the field only shows a number when there are
 * several games to count. The block board always has four. Add/sub then writes `intLayer` into it on every dealt board, which
 * brings it back from the first START of a game onwards.
 */
function useScoreboardGame(
  game: RulesGame,
  showsLayer: boolean,
): number | null {
  // The position a board was last dealt in. Keyed by position so a new game,
  // which legacy enters through `startGame`, hides the field again.
  const position = `${game.round}:${game.game}`;
  const [dealtIn, setDealtIn] = useState<string | null>(null);

  // Adjusting state during render, rather than in an effect, so the field
  // never paints a frame behind the board.
  if (game.phase === 'playing' && dealtIn !== position) {
    setDealtIn(position);
  } else if (game.phase === 'practice' && dealtIn !== null) {
    // Re-picking the same round from the chooser starts a fresh game.
    setDealtIn(null);
  }

  const singleGame = game.config.gamesPerRound === 1;

  if (!showsLayer) {
    return singleGame ? null : game.game;
  }

  return singleGame && dealtIn !== position ? null : game.layer;
}
